// services/truth/buildTruthPayload.ts
import type { AnalysisResult } from "../../types";
import type { TruthPayload, EvidenceAtom, MetricId } from "../../types";
import { renderPublicNarratives } from "../narration/renderPublicNarratives";

/**
 * Stable stringify (no external dependency).
 * Ensures deterministic key order for hashing.
 */
function stableStringify(obj: any): string {
  const seen = new WeakSet();
  const sorter = (a: string, b: string) => (a < b ? -1 : a > b ? 1 : 0);

  const walk = (value: any): any => {
    if (value === null || typeof value !== "object") return value;

    if (seen.has(value)) return "[Circular]";
    seen.add(value);

    if (Array.isArray(value)) return value.map(walk);

    const keys = Object.keys(value).sort(sorter);
    const out: Record<string, any> = {};
    for (const k of keys) out[k] = walk(value[k]);
    return out;
  };

  return JSON.stringify(walk(obj));
}

async function sha256Hex(input: string): Promise<string> {
  const data = new TextEncoder().encode(input);
  const digest = await crypto.subtle.digest("SHA-256", data.buffer);
  const bytes = new Uint8Array(digest);
  return Array.from(bytes).map(b => b.toString(16).padStart(2, "0")).join("");
}

function atom(
  metric_id: MetricId,
  value: number | string,
  unit: EvidenceAtom["unit"],
  source_path: string,
  extra?: Partial<EvidenceAtom>
): EvidenceAtom {
  return {
    metric_id,
    value,
    unit,
    source_path,
    ...extra,
  };
}

function buildFacts(result: AnalysisResult): Partial<Record<MetricId, EvidenceAtom>> {
  const facts: Partial<Record<MetricId, EvidenceAtom>> = {};

  const score = result.session?.score;
  const matchScore = result.session?.match_score;
  const qualityScore = result.session?.quality_score;

  const m = result.session?.metrics;

  // SCORE_TOTAL
  if (typeof score === "number") {
    facts.SCORE_TOTAL = atom("SCORE_TOTAL", Math.round(score), "score", "session.score");
  }

  // MATCH_SCORE / QUALITY_SCORE
  if (typeof matchScore === "number") {
    facts.MATCH_SCORE = atom("MATCH_SCORE", Math.round(matchScore), "score", "session.match_score");
  }
  if (typeof qualityScore === "number") {
    facts.QUALITY_SCORE = atom("QUALITY_SCORE", Math.round(qualityScore), "score", "session.quality_score");
  }

  // STABILITY_PCT (using synchronization metric)
  if (m && typeof m.synchronization === "number") {
    facts.STABILITY_PCT = atom(
      "STABILITY_PCT",
      Math.round(m.synchronization),
      "%",
      "session.metrics.synchronization"
    );
  }

  // CONFIDENCE_PCT
  if (m && typeof m.confidence === "number") {
    facts.CONFIDENCE_PCT = atom(
      "CONFIDENCE_PCT",
      Math.round(m.confidence),
      "%",
      "session.metrics.confidence"
    );
  }

  // TIMING_AVG_MS (derive from debug timingDeviationMs if available)
  const dbg: any = m?.debug;
  const devs: unknown = dbg?.timingDeviationMs;

  if (Array.isArray(devs) && devs.length >= 4 && devs.every(v => typeof v === "number")) {
    const avg = (devs as number[]).reduce((a, b) => a + b, 0) / devs.length;
    facts.TIMING_AVG_MS = atom(
      "TIMING_AVG_MS",
      Math.round(avg),
      "ms",
      "session.metrics.debug.timingDeviationMs",
      { computed_from: [] }
    );
  }

  return facts;
}

export async function buildTruthPayload(result: AnalysisResult): Promise<TruthPayload> {
  const sessionId = String(
    result.audit_trail?.scoring_ruleset_id
      ? result.audit_trail.scoring_ruleset_id.replace("GSC_SESSION_", "")
      : "unknown"
  );

  const facts = buildFacts(result) as Record<MetricId, EvidenceAtom>;
  const outputs = renderPublicNarratives({ result, facts });

  const facts_hash = await sha256Hex(stableStringify({ facts, outputs, sessionId }));

  return {
    version: "TRUTH_V1",
    session_id: sessionId,
    computed_at: new Date().toISOString(),
    facts,
    outputs,
    facts_hash,
  };
}
